import GATSBY_COMPILED_MDX from "/home/runner/work/capsulecrm.com/capsulecrm.com/src/mdxPages/blog/2020-06-22-june-product-updates/index.mdx";
import React, {useEffect} from "react";
import {graphql} from "gatsby";
import MainLayout from "@/layouts/MainLayout";
import MDXContentWrapper from "@/components/MDXContentWrapper";
import BlogPostHeader from "@/components/BlogPostHeader";
import SocialShare from "@/components/SocialShare";
import Longform from "@/components/Longform";
import NewsletterSignup from "@/components/NewsletterSignup";
import {Link, useTranslation} from "@zestia/gatsby-plugin-react-i18next";
import {getImage} from "gatsby-plugin-image";
import {gtmTracking} from "@/utils/gtm-tracking";
import SharedHead from "@/components/SharedHead";
import BlogStructuredData from "@/components/BlogStructuredData";
import ArticleNavigation from "@/components/ArticleNavigation";
import ScrollSpy from "@/components/ScrollSpy";
import BlogPostPreviewRow from "@/components/BlogPostPreviewRow";
import IntegrationsShowcase from "@/components/IntegrationsShowcase";
import transformBlogPosts from "@/utils/transformBlogPosts";
export const Head = props => {
  const {data} = props;
  const {title, hidden, description, summary, social_image} = data.mdx.frontmatter;
  const socialImage = social_image ? getImage(social_image.src).images.fallback.src : null;
  return React.createElement(SharedHead, Object.assign({}, props, {
    title: title,
    description: description || summary,
    image: socialImage,
    noIndex: hidden,
    appendTitle: false
  }), !hidden && React.createElement(BlogStructuredData, {
    post: data.mdx,
    imageUrl: socialImage
  }));
};
function BlogPostLayout({data, children}) {
  const {t} = useTranslation();
  const {mdx} = data;
  const {subHeadings} = data.mdx.fields;
  let relatedBlogposts = transformBlogPosts(data).sort((a, b) => {
    const dateA = a.frontmatter?.date || a.publishedAt;
    const dateB = b.frontmatter?.date || b.publishedAt;
    return new Date(dateB) - new Date(dateA);
  }).slice(0, 4);
  const overrides = {
    IntegrationsHighlight: props => {
      return React.createElement(IntegrationsShowcase, {
        title: props.title || "Integrate with the tools you already use",
        description: props.description || "Get more done in a day by connecting Capsule to all your favorite software - giving you one complete view of your customer."
      });
    },
    Link: props => {
      if (props.to === "/signup/") {
        return React.createElement(Link, Object.assign({}, props, {
          onClick: () => gtmTracking("CTA Click", "Try Free", "Blog Post")()
        }));
      }
      return React.createElement(Link, props);
    }
  };
  return React.createElement(MainLayout, {
    className: "blog-post",
    availableSlugs: mdx.fields.availableSlugs,
    trackingData: {
      intent: mdx.frontmatter.intent,
      content_category: `blog-${mdx.frontmatter.categories[0]}`
    }
  }, React.createElement("div", {
    className: "container-fluid"
  }, React.createElement("section", {
    className: "blog-post__top"
  }, React.createElement(Link, {
    to: "/blog",
    className: "text-link text-link--arrow-before"
  }, t("blog.back-to-all-posts")), React.createElement(BlogPostHeader, {
    post: data.mdx
  })), React.createElement("section", {
    className: "blog-post__content-wrapper"
  }, subHeadings.length > 0 && React.createElement("div", {
    className: "blog-post__navigation-wrapper"
  }, subHeadings.length <= 10 && React.createElement(ArticleNavigation, {
    links: subHeadings
  }), React.createElement(ArticleNavigation, {
    links: subHeadings,
    mobile: "true"
  })), React.createElement(Longform, {
    className: `blog-post__content ${!subHeadings.length || subHeadings.length > 10 ? "blog-post__content--centered" : ""}`
  }, React.createElement(ScrollSpy, {
    options: {
      limitToQuerySelector: "h2",
      offset: 80
    }
  }, React.createElement(MDXContentWrapper, {
    overrides: overrides
  }, children)), React.createElement(NewsletterSignup, {
    className: "mt-10 mb-10 newsletter-signup--no-border-bottom newsletter-signup--no-padding-bottom"
  })), React.createElement("div", {
    className: "blog-post__social-share-wrapper"
  }, React.createElement(SocialShare)))), React.createElement("div", {
    className: "container-fluid blog-post__related-blogposts pt-16 pb-16"
  }, React.createElement(BlogPostPreviewRow, {
    posts: relatedBlogposts,
    title: t("blog.related-posts")
  })));
}
export const query = graphql`
  query ($language: String, $id: String!, $categories: [String]) {
    ...Locales
    ...Integrations

    mdx(id: { eq: $id }) {
      fields {
        availableSlugs {
          lang
          slug
        }
        subHeadings {
          id
          text
        }
      }
      internal {
        type
      }
      frontmatter {
        author
        date
        social_image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, formats: AUTO)
            }
          }
        }
        social_video
        slug
        summary
        description
        title
        categories
        hidden
        intent
      }
      body
      id
    }

    allMdx(
      filter: {
        fields: {
          lang: { eq: $language }
          path: { regex: "/(mdxPages/blog)/" }
        }
        frontmatter: { hidden: { ne: true }, categories: { in: $categories } }
        id: { ne: $id }
      }
    ) {
      nodes {
        internal {
          type
        }
        frontmatter {
          title
          date
          categories
          slug
          preview_image {
            alt
            src {
              childImageSharp {
                gatsbyImageData(formats: AUTO, width: 400, placeholder: BLURRED)
              }
            }
          }
        }
      }
    }

    allSanityBlogPost(
      filter: {
        categories: { in: $categories }
        _id: { ne: $id }
        hidden: { ne: true }
        hiddenFromPageOnly: { ne: true }
        language: { eq: $language }
      }
    ) {
      nodes {
        internal {
          type
        }
        title
        publishedAt
        categories
        slug {
          current
        }
        coverImage {
          ...SanityImageData
        }
      }
    }

    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "blog/generic-image.png" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`;
BlogPostLayout
export default function GatsbyMDXWrapper(props) {
  return React.createElement(BlogPostLayout, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
